import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { withRouter } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactPaginate from 'react-paginate';
import {
  showAllStrategies,
  showStrategyEditor,
  showPersonalStrategy,
  closeStrategyEditor,
  updateStategyDetails,
  saveNewStrategy,
  loadPersonalStrategiesFromNew,
  PORT_HUB_TYPES,
} from './../../../actions/data.js';
import './StrategyList.css';
import i18n from './../../../i18n';
import StrategyRow from './StrategyRow.js';
import StrategyEditor from 'wt-react/dist/components/StrategyEditor/StrategyEditor';
import 'react-tabs/style/react-tabs.css';
import { formatDate } from './../../../common';

export const KEY_METRICS = ['performance', 'income', 'risk', 'sharpe', 'fees'];

// sort
const FILTER_LABELS = {
  'user-favorite': 'Subscribed Models',
  'managed-cio': 'Shared CIO Models',
  'user-custom-model': 'Models On Demand',
  'user-model': 'User Built Portfolios',
};

class StrategyList extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      menuIsOpen: false,
      currPage: 0,
      numPerPage: 10,
      searchTerm: '',
      savingNewStrategy: false,
      savingNewStrategyValue: null,
      userSelectedTab: false,
      didAutoSelectDefaultTab: false,
      // Default filter = Favorites (as requested)
      filterType: ['user-favorite'],
    };

    this.paginate = this.paginate.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    // Keep tooltips positioned after any layout changes
    ReactTooltip.hide();
    ReactTooltip.rebuild();

    const data = this.props.data || {};
    const prevData = prevProps.data || {};
    const strategyIdToShow = data.strategyIdToShow;
    const sectionsSummary = data.sectionsSummary || {};
    const prevSectionsSummary = prevData.sectionsSummary || {};

    const getSectionCount = (sections, type) => {
      const section = sections[type];
      return section && Array.isArray(section.data) ? section.data.length : 0;
    };

    const getFirstNonEmptyTab = (sections) => {
      const hasSubscribed = getSectionCount(sections, 'user-favorite') > 0;
      const hasSharedCio = getSectionCount(sections, 'managed-cio') > 0;
      const hasOnDemand = getSectionCount(sections, 'user-custom-model') > 0;
      const hasUserBuilt = getSectionCount(sections, 'user-model') > 0;

      if (hasSubscribed) return 'user-favorite';
      if (hasSharedCio) return 'managed-cio';
      if (hasOnDemand) return 'user-custom-model';
      if (hasUserBuilt) return 'user-model';
      // If nothing has data, fall back to User Built Portfolios (so the user can create one)
      return 'user-model';
    };

    // Auto-select the default tab after all 4 sections resolve, based on which tabs have data.
    // Priority rules:
    // - If Subscribed has data => Subscribed (user-favorite)
    // - Else if Shared CIO has data => Shared CIO (managed-cio)
    // - Else if Models on Demand has data => Models on Demand (user-custom-model)
    // - Else if User Built has data => User Built (user-model)
    // - Else (no data across all 4 tabs) => All (no filter)
    if (
      !this.state.didAutoSelectDefaultTab &&
      !this.state.userSelectedTab &&
      sectionsSummary.allResolved &&
      !prevSectionsSummary.allResolved
    ) {
      const sections = data.personalStrategiesSections || {};
      const desiredDefault = getFirstNonEmptyTab(sections);
      const desiredFilterType = desiredDefault ? [desiredDefault] : ['user-model'];

      const current = (this.state.filterType && this.state.filterType[0]) || null;
      const currentFilterType = this.state.filterType || [];
      const currentKey = current || null;
      if (
        (desiredDefault && currentKey !== desiredDefault) ||
        (!desiredDefault && currentFilterType.length > 0)
      ) {
        this.props.showAllStrategies();
        this.setMenuIsOpen(false);
        this.setState({
          filterType: desiredFilterType,
          currPage: 0,
          didAutoSelectDefaultTab: true,
        });
      } else {
        this.setState({ didAutoSelectDefaultTab: true });
      }
    }

    // If Subscribed or Shared CIO is selected but resolves empty, fall back to a tab with data (or All).
    if (sectionsSummary.allResolved) {
      const sections = data.personalStrategiesSections || {};
      const current = (this.state.filterType && this.state.filterType[0]) || null;
      const currentLower = String(current || '')
        .trim()
        .toLowerCase();
      const isHiddenWhenEmpty =
        currentLower === 'user-favorite' || currentLower === 'managed-cio';

      if (isHiddenWhenEmpty && getSectionCount(sections, currentLower) === 0) {
        const desiredDefault = getFirstNonEmptyTab(sections);
        const desiredFilterType = desiredDefault ? [desiredDefault] : ['user-model'];
        if (JSON.stringify(this.state.filterType || []) !== JSON.stringify(desiredFilterType)) {
          this.props.showAllStrategies();
          this.setMenuIsOpen(false);
          this.setState({ filterType: desiredFilterType, currPage: 0 });
        }
      }
    }

    // Open from URL once strategies are available
    if (data.personalStrategies !== prevData.personalStrategies) {
      const ps = data.personalStrategies;
      if (Array.isArray(ps) && ps.length > 0) {
        this.readURL();
      }
    }

    // When closing the viewer, drop id from hash
    if (
      prevData.strategyIdToShow !== strategyIdToShow &&
      (strategyIdToShow === false ||
        strategyIdToShow === null ||
        strategyIdToShow === undefined)
    ) {
      const history = this.props.history;
      const hash = (history.location.hash || '').replace(/^#/, '');

      if (hash) {
        // split the hash into parts separated by &
        const parts = hash.split('&');

        // keep only parts that DON'T start with id= or myportfolioid=
        const cleaned = parts.filter(function (p) {
          const startsWithId = p.startsWith('id=');
          const startsWithMyPortfolioId = p.startsWith('myportfolioid=');
          return !startsWithId && !startsWithMyPortfolioId;
        });

        // stitch it back together and update the URL
        const newHash = cleaned.join('&');
        history.replace({ hash: newHash });
      }
    }

    // Clamp page when inputs change (filters/search/page size/saving row/data)
    if (
      prevState.searchTerm !== this.state.searchTerm ||
      prevState.filterType !== this.state.filterType ||
      prevState.numPerPage !== this.state.numPerPage ||
      prevState.savingNewStrategy !== this.state.savingNewStrategy ||
      prevState.savingNewStrategyValue !== this.state.savingNewStrategyValue ||
      (this.props.data && this.props.data.personalStrategies) !==
        (prevProps.data && prevProps.data.personalStrategies)
    ) {
      var paging = this.computePagingData();
      if (this.state.currPage > paging.pageCount - 1) {
        this.setState({ currPage: Math.max(0, paging.pageCount - 1) });
      }
    }
  }

  readURL = () => {
    const history = this.props.history;
    const hash = (history.location.hash || '').replace(/^#/, '');
    if (!hash) return;

    // look for id=… or myportfolioid=…
    const parts = hash.split('&');
    const idParam = parts.find(
      (p) => /^id=/.test(p) || /^myportfolioid=/.test(p)
    );
    if (!idParam) return;

    const id = idParam.split('=')[1];
    if (!id) return;

    const data = this.props.data || {};
    const personalStrategies = Array.isArray(data.personalStrategies)
      ? data.personalStrategies
      : [];

    // Only auto-open once we have a concrete match from one of the loaded sections.
    // If there is no match yet, keep waiting for the remaining section loads.
    const match = personalStrategies.find((strategy) => strategy?.id === id);
    if (!match) return;

    const rawType = match.portHubType;
    const types = Array.isArray(rawType) ? rawType : rawType ? [rawType] : [];
    const normalizedTypes = types
      .map((t) =>
        String(t || '')
          .trim()
          .toLowerCase()
      )
      .filter(Boolean);
    const matchedTab =
      normalizedTypes.find((t) => PORT_HUB_TYPES.includes(t)) ||
      normalizedTypes[0];

    if (matchedTab && this.state.filterType?.[0] !== matchedTab) {
      this.setState({
        filterType: [matchedTab],
        currPage: 0,
        userSelectedTab: true,
      });
    }

    if (data.strategyIdToShow !== id) {
      this.props.showPersonalStrategy(id);
    }

    // Once matched and opened, strip id from hash so refresh does not re-open it.
    const cleaned = parts.filter((p) => {
      return !/^id=/.test(p) && !/^myportfolioid=/.test(p);
    });
    const newHash = cleaned.join('&');
    try {
      history.replace({ hash: newHash });
    } catch (_) {}
  };

  setMenuIsOpen = (menuIsOpen) => this.setState({ menuIsOpen });

  paginate({ selected }) {
    this.setState({ currPage: selected });
  }

  handleSearchChange(e) {
    this.setState({ searchTerm: e.target.value, currPage: 0 });
  }

  handleClearSearch = () => {
    this.setState({ searchTerm: '', currPage: 0 });
  };

  // --- Single-select filter buttons (radio-like) ---
  toggleFilter = (value) => {
    // 1) Close any open details
    this.props.showAllStrategies();

    // collapse any row-level menus
    this.setMenuIsOpen(false);

    // new filter and reset paging
    this.setState((prev) => {
      // Keep radio-like behavior: clicking the active tab should not clear the filter.
      return { filterType: [value], currPage: 0, userSelectedTab: true };
    });
  };

  clearFilter = () => {
    this.setState({ filterType: [], currPage: 0, userSelectedTab: true });
  };

  getSectionState = (value) => {
    if (!value) return null;
    const data = this.props.data || {};
    const sections = data.personalStrategiesSections || {};
    return sections[value] || null;
  };

  getSectionStatus = (value) => {
    const section = this.getSectionState(value);
    return section && section.status ? section.status : 'idle';
  };

  getSectionError = (value) => {
    const section = this.getSectionState(value);
    return section && section.error ? section.error : null;
  };

  setSavingStrategyInProgress = (value, isSaving) => {
    const data = this.props.data || {};
    const personalStrategies = data.personalStrategies || [];
    const updated = [value].concat(personalStrategies);

    if (!isSaving && value) {
      this.props.loadPersonalStrategiesFromNew(updated);
    }
    this.setState({
      savingNewStrategy: isSaving,
      savingNewStrategyValue: value,
    });
  };

  // Helper: pretty label using FILTER_LABELS map
  getPrettyLabel = (value) => {
    if (value == null) return '';
    const key = String(value).trim();
    return FILTER_LABELS[key] || key;
  };

  // --- Build unique type options from portHubType in data ---
  getTypeOptionsFromData = () => {
    const { data = {} } = this.props;
    const personalStrategies = data.personalStrategies || [];
    const sections = data.personalStrategiesSections || {};
    const seenLower = new Set();
    const options = [];

    const shouldShowType = (value) => {
      const key = String(value || '')
        .trim()
        .toLowerCase();

      // Hide Subscribed + Shared CIO tabs when we know they have no data.
      if (key === 'user-favorite' || key === 'managed-cio') {
        const section = sections[key];
        const status = section && section.status ? section.status : 'idle';
        if (status !== 'success') return true; // still loading / error / idle
        const count = section && Array.isArray(section.data) ? section.data.length : 0;
        return count > 0;
      }

      return true;
    };

    const addOption = (value) => {
      if (!value) return;
      const label = String(value).trim();
      if (!label) return;
      if (!shouldShowType(label)) return;
      const lower = label.toLowerCase();
      if (seenLower.has(lower)) return;
      seenLower.add(lower);
      options.push({ value: label, label });
    };

    // Always show the primary tabs in the expected order
    (PORT_HUB_TYPES || []).forEach(addOption);

    // Collect unique types from all personal strategies
    personalStrategies.forEach((strategy = {}) => {
      const raw = strategy.portHubType;
      if (!raw) return;
      const types = Array.isArray(raw) ? raw : [raw];
      types.forEach(addOption);
    });

    // Preserve your preferred order first, then alphabetical fallback
    const preferredOrder = [
      'user-favorite',
      'managed-cio',
      'user-custom-model',
      'user-model',
      ...(PORT_HUB_TYPES || []),
    ];

    const rank = (val) => {
      const i = preferredOrder.indexOf(String(val).toLowerCase());
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };

    options.sort((a, b) => {
      const ra = rank(a.value);
      const rb = rank(b.value);
      if (ra !== rb) return ra - rb;
      const la = FILTER_LABELS[a.value] || a.value;
      const lb = FILTER_LABELS[b.value] || b.value;
      return la.localeCompare(lb);
    });

    return options;
  };

  // --- Filtering (by name + portHubType) ---
  getFilteredStrategies = () => {
    const { data = {} } = this.props;
    const personalStrategies = Array.isArray(data.personalStrategies)
      ? data.personalStrategies
      : [];
    const searchTerm = (this.state.searchTerm || '').trim().toLowerCase();
    const filterType = this.state.filterType || [];

    const selectedLower = new Set(
      filterType
        .map((v) =>
          String(v || '')
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)
    );

    const noTypeFilter = selectedLower.size === 0;

    const filtered = personalStrategies.filter((strategy = {}) => {
      const name = (strategy.name || '').toLowerCase();
      const nameMatches = searchTerm === '' || name.includes(searchTerm);

      const raw = strategy.portHubType;
      if (noTypeFilter) return nameMatches;

      const typeArray = Array.isArray(raw) ? raw : raw ? [raw] : [];
      const typeMatches = typeArray.some((t) =>
        selectedLower.has(
          String(t || '')
            .trim()
            .toLowerCase()
        )
      );

      return nameMatches && typeMatches;
    });

    // Cache debug key
    const logKey = JSON.stringify({ needle: searchTerm, filterType });
    if (this._lastFilterLogKey !== logKey) {
      this._lastFilterLogKey = logKey;
    }

    return filtered;
  };

  // --- Paging (exclude saving row; keep exactly numPerPage visible) ---
  computePagingData = () => {
    const { numPerPage, currPage, savingNewStrategy, savingNewStrategyValue } =
      this.state;
    const { data = {} } = this.props;
    const { strategyIdToShow, personalStrategies = [] } = data;

    const filtered = this.getFilteredStrategies();

    // Determine if a strategy is expanded
    const isExpanded = strategyIdToShow != null;
    const expanded = isExpanded
      ? personalStrategies.find((s) => s?.id === strategyIdToShow) || null
      : null;

    // Handle saving row visibility
    const includeSavingRow = Boolean(
      savingNewStrategy && savingNewStrategyValue
    );

    const listSource = includeSavingRow
      ? filtered.filter((s) => {
          if (!savingNewStrategyValue) return true;
          if (savingNewStrategyValue.id != null)
            return s.id !== savingNewStrategyValue.id;
          if (savingNewStrategyValue.name)
            return s.name !== savingNewStrategyValue.name;
          return true;
        })
      : filtered;

    // Adjust page size if saving row is visible
    const effectivePerPage =
      !isExpanded && includeSavingRow
        ? Math.max(1, numPerPage - 1)
        : numPerPage;

    const pageCount = Math.max(
      1,
      Math.ceil(listSource.length / effectivePerPage)
    );

    const start = currPage * effectivePerPage;
    const end = start + effectivePerPage;
    const pageItems = listSource.slice(start, end);

    return {
      isExpanded,
      expanded,
      includeSavingRow,
      pageCount,
      pageItems,
    };
  };

  renderHeaders() {
    const ft = (this.state.filterType || []).map((v) =>
      String(v).toLowerCase()
    );
    const isBreakdownView =
      ft.length === 1 && (ft[0] === 'managed-cio' || ft[0] === 'user-favorite');
    const isUserBuiltTab = ft.length === 1 && ft[0] === 'user-model';
    const isModelsOnDemandTab =
      ft.length === 1 && ft[0] === 'user-custom-model';
    const useInceptionPerfTooltip =
      ft.length === 1 &&
      (ft[0] === 'user-favorite' ||
        ft[0] === 'managed-cio' ||
        ft[0] === 'user-custom-model');
    const filteredStrategies = this.getFilteredStrategies();
    const perfAsOf =
      useInceptionPerfTooltip && filteredStrategies.length
        ? filteredStrategies[0].commonPeriodEnd ||
          filteredStrategies[0].updateDate ||
          null
        : null;
    const perfAsOfFormatted = perfAsOf ? formatDate(perfAsOf) : null;
    const inceptionAsOfLabel = perfAsOfFormatted || 'latest available date';

    return (
      <div className="headers">
        <div className="menu title">{i18n.t('name')}</div>
        <div className="name title"></div>
        <div className="inception-date title">
          <div className="title-line">Inception</div>
          <div className="title-line">Date</div>
        </div>

        {/* Statistics header: in Favorites/CIO hide 'sharpe' and label 'risk' as Asset Class */}
        <div className={'statistics' + (isBreakdownView ? ' asset-2col' : '')}>
          {(isBreakdownView
            ? ['performance', 'income', 'risk', 'fees'] // no 'sharpe' here
            : KEY_METRICS
          ).map((metric) => (
            <div className={'stat-' + metric} key={'stat-' + metric}>
              <div className="stat-heading">
                <div className="title">
                  {isBreakdownView && metric === 'risk' ? (
                    'Asset Class'
	                  ) : metric === 'fees' && !isUserBuiltTab ? (
	                    <div className="fee-title">
	                      <div className="title-line">Expense</div>
	                      <div className="title-line">Ratio</div>
	                    </div>
	                  ) : (
	                    i18n.t(metric)
	                  )}
	                  {metric === 'performance' && !isUserBuiltTab && (
	                    <div className="stat-subtitle">
	                      Since inception
	                    </div>
	                  )}
                </div>
	                {!(isBreakdownView && metric === 'risk') &&
	                  !(metric === 'performance' && !isUserBuiltTab) && (
	                    <div className="more-info">
	                      <FontAwesomeIcon
	                        data-tip
	                        data-for={metric + '-desc'}
                        icon="question-circle"
                        size="sm"
                      />
                    </div>
                  )}
              </div>
              {!(isBreakdownView && metric === 'risk') &&
                !(metric === 'performance' && !isUserBuiltTab) && (
                <ReactTooltip
                  id={metric + '-desc'}
                  type="light"
                  className="tooltip"
                  effect="float"
                    scrollHide={true}
                    resizeHide={true}
                    globalEventOff="click"
                    delayHide={100}
                  >
                    {isBreakdownView && metric === 'risk'
                      ? 'Equity / Fixed Income / Alternatives breakdown'
                      : metric === 'income'
                      ? 'Trailing 12M Dividend Yields (%)'
                      : metric === 'performance' && useInceptionPerfTooltip
                      ? `Since inception As of ${inceptionAsOfLabel}`
                      : metric === 'risk' && isModelsOnDemandTab
                      ? `Avg Annual Volatility Since Inception As of ${inceptionAsOfLabel}`
		                      : metric === 'sharpe' && isModelsOnDemandTab
		                      ? 'Avg Annual Sharpe Ratio Since Inception'
		                      : metric === 'fees'
		                      ? i18n.t('fees-tooltip')
		                      : i18n.t(metric + '-desc')}
                  </ReactTooltip>
                )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderSearchAndFilter(showSearchRow = true) {
    const { filterType = [], searchTerm = '' } = this.state;
    const selected = Object.fromEntries(filterType.map((f) => [f, true]));
    const typeOptions = this.getTypeOptionsFromData(); // from portHubType

    return (
      <div
        className="search-container"
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {/* Row 1: filter buttons */}
        <div className="filter-row">
          {typeOptions.map((opt) => {
            const isActive = !!selected[opt.value];
            const status = this.getSectionStatus(opt.value);
            const isLoading = status === 'loading';
            const hasError = status === 'error';

            const buttonClass = [
              'filter',
              isActive && 'filter-active',
              hasError && 'filter-has-error',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => this.toggleFilter(opt.value)}
                className={buttonClass}
                aria-pressed={isActive}
                title={opt.label}
              >
                <span className="filter-label">
                  {this.getPrettyLabel(opt.value)}
                </span>
                {isLoading && (
                  <FontAwesomeIcon
                    icon="circle-notch"
                    size="sm"
                    spin
                    className="filter-status-icon"
                  />
                )}
                {hasError && (
                  <FontAwesomeIcon
                    icon="exclamation-triangle"
                    size="sm"
                    className="filter-status-icon filter-error-icon"
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Row 2: search + clear */}
        {showSearchRow && (
          <div className="search-row">
            <div
              style={{
                flex: 1,
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
              }}
            >
              <input
                type="text"
                placeholder="Search by Portfolio Name"
                value={searchTerm}
                onChange={this.handleSearchChange}
                className="search-input"
              />
              <button onClick={this.handleClearSearch} className="clear-button">
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderTabLoadingState(label) {
    return (
      <div className="tab-state tab-state-loading center-spinner">
        <FontAwesomeIcon icon="circle-notch" size="3x" spin />
      </div>
    );
  }

  renderTabErrorState(label, message) {
    return (
      <div className="tab-state tab-state-error center-spinner">
        <FontAwesomeIcon icon="exclamation-triangle" size="2x" />
        <div className="tab-state-message">{message || `Unable to load`}</div>
      </div>
    );
  }

  handleGoToModelCenter = () => {
    window.location.assign('/portfolio-solutions/model-portfolios');
  };

  renderTabEmptyState(activeFilter) {
    if (activeFilter === 'user-favorite') {
      return (
        <div className="no-strategies">
          <div className="no-strategies-label">
            <h4>
              No Data. Click to Visit our{' '}
              <button
                type="button"
                className="model-center-link"
                onClick={this.handleGoToModelCenter}
              >
                Model Center
              </button>
              .
            </h4>
          </div>
        </div>
      );
    }

    return (
      <div className="no-strategies">
        <div className="no-strategies-label">
          <h4>No data available for display.</h4>
        </div>
      </div>
    );
  }

  render() {
    const strategiesLoadError = this.props.strategiesLoadError;
    const data = this.props.data || {};
    const params = this.props.params || {};
    const sectionsSummary = data.sectionsSummary || {};
    const sections = data.personalStrategiesSections || {};
    const hasAnyCompletion = !!sectionsSummary.hasAnyCompletion;
    const allResolved = !!sectionsSummary.allResolved;

    if (strategiesLoadError) {
      return (
        <div className="no-strategies">
          <div className="no-strategies-label">
            <h4>
              {i18n.t('error-loading-strategies') || 'Error Loading Portfolios'}
            </h4>
          </div>
        </div>
      );
    }

    const personalStrategies = data.personalStrategies;
    if (!hasAnyCompletion || personalStrategies == null) {
      return (
        <div className="loading-strategies-hub">
          <FontAwesomeIcon icon="circle-notch" size="3x" spin />
        </div>
      );
    }

    const filterType = this.state.filterType || [];
    const activeFilter = filterType.length > 0 ? filterType[0] : null;
    const activeSection = activeFilter ? sections[activeFilter] || {} : null;
    const activeStatus = activeSection ? activeSection.status : null;
    const activeError = activeSection ? activeSection.error : null;
    const shouldShowSearchRow = allResolved;
    const shouldShowFilters = !this.state.menuIsOpen;

    const strategyIdToShow = data.strategyIdToShow;
    const strategySaveInProgress = data.strategySaveInProgress;
    const haveList =
      Array.isArray(personalStrategies) && personalStrategies.length > 0;
    const filteredStrategies = this.getFilteredStrategies();

    const headings = !this.state.menuIsOpen ? (
      <div className="row">
        <div className="title-heading col-sm-12">{i18n.t('title-heading')}</div>
        <div className="title-subheading col-sm-12">
          {i18n.t('title-subheading')}
        </div>
      </div>
    ) : null;

    const shouldShowNoStrategies =
      sectionsSummary.allSuccessEmpty &&
      !this.state.savingNewStrategy &&
      !haveList;

    const noDataForTab =
      activeStatus === 'success' &&
      !this.state.savingNewStrategy &&
      filteredStrategies.length === 0;

    if (shouldShowNoStrategies) {
      return (
        <div className="no-strategies">
          <div className="no-strategies-label">
            <h4>{i18n.t('no-strategies')}</h4>
          </div>
          <div className="new-strategy">
            <StrategyEditor
              key={'new-strategy-editor-1'}
              type={'create'}
              buttonLabel={i18n.t('new-strategy')}
              showEditor={true}
              token={params.token}
              clientEmail={params.clientEmail}
              dataspanApiUrl={process.env.REACT_APP_API_URL || ''}
              benchmarks={[]}
              checkTickersUrl={process.env.REACT_APP_DPD_URL || ''}
              typeHeadPlaceholder={'Search By Ticker or Name'}
              showInfoModal={true}
              closeModalOnSave={true}
              savedStrategyCallBack={(value, isSaving) =>
                this.setSavingStrategyInProgress(value, isSaving)
              }
            />
          </div>
        </div>
      );
    }

    const prettyFilterLabel = this.getPrettyLabel(activeFilter);

    if (activeFilter && activeStatus === 'loading') {
      return (
        <div className="strategylistPS">
          {headings}
          {shouldShowFilters && this.renderSearchAndFilter(shouldShowSearchRow)}
          {this.renderTabLoadingState(prettyFilterLabel)}
        </div>
      );
    }

    if (activeFilter && activeStatus === 'error') {
      return (
        <div className="strategylistPS">
          {headings}
          {shouldShowFilters && this.renderSearchAndFilter(shouldShowSearchRow)}
          {this.renderTabErrorState(prettyFilterLabel, activeError)}
        </div>
      );
    }

    if (activeFilter && noDataForTab) {
      return (
        <div className="strategylistPS">
          {headings}
          {shouldShowFilters && this.renderSearchAndFilter(shouldShowSearchRow)}
          {this.renderTabEmptyState(activeFilter)}
        </div>
      );
    }

    // compute the breakdown flag here as well, so we can tag a page-level class if needed later
    const ft = (filterType || []).map((v) => String(v).toLowerCase());
    const isBreakdownView =
      ft.length === 1 && (ft[0] === 'managed-cio' || ft[0] === 'user-favorite');

    if (this.state.savingNewStrategy || haveList) {
      const { isExpanded, expanded, includeSavingRow, pageItems, pageCount } =
        this.computePagingData();

      return (
        <div
          className={`strategylistPS${
            isBreakdownView ? ' breakdown-view' : ''
          }`}
        >
          {headings}

          {shouldShowFilters && this.renderSearchAndFilter(shouldShowSearchRow)}

          {!strategyIdToShow && this.renderHeaders()}

          {/* Saving row while new strategy is being persisted */}
          {includeSavingRow && !isExpanded && (
            <StrategyRow
              key="saving-row"
              personalStrategy={this.state.savingNewStrategyValue}
              openMenu={false}
              striped={1}
              saveInProgress
              menuIsOpen={false}
              setMenuIsOpen={null}
            />
          )}

          {/* Expanded single-strategy view */}
          {isExpanded && expanded ? (
            <StrategyRow
              key={expanded.id}
              personalStrategy={expanded}
              openMenu
              striped={false}
              breakdownView={isBreakdownView}
              saveInProgress={
                !!strategySaveInProgress &&
                strategySaveInProgress === expanded.id
              }
              menuIsOpen={this.state.menuIsOpen}
              setMenuIsOpen={this.setMenuIsOpen}
            />
          ) : (
            pageItems.map((strategy, index) => (
              <StrategyRow
                key={strategy.id}
                personalStrategy={strategy}
                openMenu={false}
                striped={index % 2 !== 0}
                breakdownView={isBreakdownView}
                saveInProgress={
                  !!strategySaveInProgress &&
                  strategySaveInProgress === strategy.id
                }
                menuIsOpen={this.state.menuIsOpen}
                setMenuIsOpen={this.setMenuIsOpen}
              />
            ))
          )}

          {/* Pagination + new strategy editor (only in list view) */}
          {!isExpanded && (
            <div className="last-row">
              <div className="row-pagination">
                <ReactPaginate
                  previousLabel={
                    <FontAwesomeIcon icon="chevron-left" size="xs" />
                  }
                  nextLabel={<FontAwesomeIcon icon="chevron-right" size="xs" />}
                  pageCount={pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={this.paginate}
                  containerClassName="pagination"
                  pageClassName="page-item"
                  pageLinkClassName="page-item"
                  previousClassName="page-item"
                  previousLinkClassName="pageLink"
                  nextClassName="page-item"
                  nextLinkClassName="pageLink"
                  activeClassName="active"
                  disabledClassName="disabled"
                  forcePage={this.state.currPage}
                />
              </div>

              <div className="new-strategy">
                <StrategyEditor
                  key="new-strategy-editor-1"
                  type="create"
              buttonLabel={i18n.t('new-strategy')}
              showEditor
              token={params.token}
              clientEmail={params.clientEmail}
              dataspanApiUrl={process.env.REACT_APP_API_URL || ''}
              benchmarks={[]}
              checkTickersUrl={process.env.REACT_APP_DPD_URL || ''}
              typeHeadPlaceholder="Search By Ticker or Name"
              savedStrategyCallBack={(value, isSaving) =>
                this.setSavingStrategyInProgress(value, isSaving)
              }
                  closeModalOnSave
                  showInfoModal={false}
                />
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="loading-strategies-hub">
        <FontAwesomeIcon icon="circle-notch" size="2x" spin />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    data: state.data,
    params: state.params,
    strategiesLoadError: state.data.strategiesLoadError,
  };
}

export default withRouter(
  connect(mapStateToProps, {
    showAllStrategies,
    showStrategyEditor,
    showPersonalStrategy,
    closeStrategyEditor,
    updateStategyDetails,
    saveNewStrategy,
    loadPersonalStrategiesFromNew,
  })(StrategyList)
);
